import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Save, Edit3, X, UserCheck } from "lucide-react";
import { ProgressRing } from "@/components/crm/UiBits";
import ActivityLogPanel from "@/components/crm/ActivityLogPanel";
import {
  TRACKS, TRACK_FIELD, sessionTitle, sessionDescription, todayIso, TOTAL_LESSON_COUNT,
} from "@/lib/tracks";
import { DescriptionCell } from "@/components/DescriptionCell";
import GenderField from "@/components/GenderField";
import ConnectionLog from "@/components/crm/ConnectionLog";
import { formatDisplayName } from "@/lib/names";

function ProgressTrack({ track, sessions, onToggle, resources = {}, editable = true, myTeacherId = null }) {
  const Icon = track.icon;
  const rawDone = sessions.filter((s) => s.completed).length;
  const required = track.requiredCount ?? track.count;
  const done = Math.min(rawDone, required);
  const eitherOr = required < track.count;
  const showItemSubtitle = track.key !== "initial_contact";
  const pct = (done / required) * 100;
  return (
    <div className="mb-6" data-testid={`teacher-track-${track.key}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="shrink-0 flex items-center justify-center rounded-md" style={{ width: 32, height: 32, background: "var(--surface-hover)", color: track.color }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <h4 className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>{track.label}</h4>
        <span className="text-xs" style={{ color: "var(--muted)" }}>{done}/{required}</span>
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-base)" }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: track.color, transition: "width 0.3s ease" }} />
        </div>
      </div>
      {eitherOr && (
        <p className="text-xs mb-2 pl-11" style={{ color: "var(--muted)" }} data-testid={`teacher-track-${track.key}-hint`}>
          {required === 1
            ? <>Pick <strong>one</strong> lesson — either satisfies this section.</>
            : <>Complete <strong>any {required} of {track.count}</strong> to satisfy this section.</>}
        </p>
      )}
      <div className="space-y-2">
        {sessions.map((s, i) => {
          const byOther = !!(s.by_teacher_id && myTeacherId && s.by_teacher_id !== myTeacherId && s.by_teacher_name);
          return (
          <div key={i} className="flex items-start gap-3 p-3 rounded-md border" style={{ borderColor: s.completed ? "var(--border-strong)" : "var(--border-subtle)", background: s.completed ? "var(--surface-hover)" : "transparent" }} data-testid={`teacher-session-${track.key}-${i}`}>
            <button
              onClick={() => editable && onToggle(track.key, i, !s.completed, s.date || todayIso(), s.notes || "")}
              disabled={!editable}
              className="mt-1 shrink-0"
              data-testid={`teacher-toggle-${track.key}-${i}`}
              aria-label={`Toggle ${track.key} session ${i + 1}`}
              style={editable ? {} : { cursor: "not-allowed" }}
            >
              {s.completed ? <CheckCircle2 size={20} style={{ color: track.color }} /> : <Circle size={20} style={{ color: "var(--muted)" }} />}
            </button>
            <div className="w-48 shrink-0">
              <LessonTitle
                track={track}
                index={i}
                resources={resources}
              />
              {sessionDescription(track.key, i) && (
                <DescriptionCell
                  title={sessionTitle(track.key, i)}
                  description={sessionDescription(track.key, i)}
                  testid={`teacher-desc-${track.key}-${i}`}
                />
              )}
              {showItemSubtitle && !sessionDescription(track.key, i) && (
                <div className="text-[0.65rem] uppercase tracking-wider mt-0.5" style={{ color: "var(--muted)" }}>Session {i + 1}</div>
              )}
              {s.completed && (
                <input
                  type="date"
                  value={s.date || todayIso()}
                  onChange={(e) => editable && onToggle(track.key, i, true, e.target.value, s.notes || "")}
                  readOnly={!editable}
                  data-testid={`teacher-date-${track.key}-${i}`}
                  className="mt-2 text-xs px-2 py-1 w-full"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <textarea
                rows={2}
                placeholder={editable ? `Notes for "${sessionTitle(track.key, i)}"…` : "(read-only)"}
                defaultValue={s.notes || ""}
                onBlur={(e) => {
                  if (!editable) return;
                  const next = e.target.value;
                  if (next !== (s.notes || "")) {
                    onToggle(track.key, i, s.completed, s.date || (s.completed ? todayIso() : null), next);
                  }
                }}
                readOnly={!editable}
                data-testid={`teacher-notes-${track.key}-${i}`}
                className="w-full text-xs px-2 py-1.5"
                style={editable ? {} : { background: "var(--bg-base)" }}
              />
              {byOther && (
                <div
                  className="mt-1 inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                  style={{ background: "var(--surface-hover)", color: "var(--ink-2)", border: "1px solid var(--border-subtle)" }}
                  data-testid={`teacher-session-by-${track.key}-${i}`}
                  title={`Last updated by ${s.by_teacher_name}`}
                >
                  <UserCheck size={9} /> by {s.by_teacher_name}
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function LessonTitle({ track, index, resources }) {
  const url = resources[`${track.key}:${index}:teacher_notes`] || "";
  const title = sessionTitle(track.key, index);
  return (
    <div data-testid={`teacher-lesson-title-${track.key}-${index}`}>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-sm font-medium leading-tight hover:underline" style={{ color: "var(--gold)" }} title={`Open "${title}" lesson file`} data-testid={`teacher-lesson-link-${track.key}-${index}`}>
          {title}
        </a>
      ) : (
        <span className="text-sm font-medium leading-tight" style={{ color: "var(--ink)" }}>{title}</span>
      )}
    </div>
  );
}

function GuestInfoCard({ guest, onSaved, editable = true, overallDone }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: guest.first_name || "",
    last_name: guest.last_name || "",
    gender: guest.gender || null,
    email: guest.email || "",
    phone: guest.phone || "",
    city: guest.city || "",
    address: guest.address || "",
    notes: guest.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/teacher/guests/${guest.id}`, form);
      toast.success("Saved.");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <div className="card-surface p-6" data-testid="teacher-guest-info">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <ProgressRing value={overallDone} total={TOTAL_LESSON_COUNT} size={54} stroke={5} color="var(--ember)" label="Overall progress" />
            <div className="min-w-0">
              <div className="overline mb-1">Guest</div>
              <h2 className="font-serif-display text-2xl lg:text-3xl leading-tight" style={{ color: "var(--ink)" }}>
                {formatDisplayName(guest)}
              </h2>
              <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                {overallDone} of {TOTAL_LESSON_COUNT} sessions complete
              </div>
            </div>
          </div>
          {editable && (
            <button onClick={() => setEditing(true)} className="btn-ghost" data-testid="teacher-edit-guest-btn">
              <Edit3 size={14} /> Edit
            </button>
          )}
        </div>
        <dl className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {guest.email && <div><dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Email</dt><dd style={{ color: "var(--ink)" }}>{guest.email}</dd></div>}
          {guest.phone && <div><dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Phone</dt><dd style={{ color: "var(--ink)" }}>{guest.phone}</dd></div>}
          {guest.city && <div><dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>City</dt><dd style={{ color: "var(--ink)" }}>{guest.city}</dd></div>}
          {guest.address && <div><dt className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Address</dt><dd style={{ color: "var(--ink)" }}>{guest.address}</dd></div>}
        </dl>
        {guest.notes && (
          <div className="mt-4 p-3 rounded-md" style={{ background: "var(--surface-hover)", borderLeft: "3px solid var(--ember)" }}>
            <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Notes</div>
            <p className="text-sm whitespace-pre-line" style={{ color: "var(--ink)" }}>{guest.notes}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card-surface p-6" data-testid="teacher-guest-edit">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>Edit guest</h2>
        <button onClick={() => setEditing(false)} className="p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[["first_name", "First name *"], ["last_name", "Last name"], ["email", "Email", "email"], ["phone", "Phone"], ["city", "City"], ["address", "Address"]].map(([k, label, type]) => (
          <div key={k}>
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>{label}</label>
            <input type={type || "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid={`teacher-edit-${k}`} />
          </div>
        ))}
        <div className="md:col-span-2">
          <GenderField value={form.gender} onChange={(g) => setForm({ ...form, gender: g })} testid="teacher-edit-gender" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Notes</label>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 text-sm" data-testid="teacher-edit-notes" />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={save} disabled={busy} className="btn-primary" data-testid="teacher-save-guest-btn"><Save size={14} /> {busy ? "Saving…" : "Save"}</button>
        <button onClick={() => setEditing(false)} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
}

export default function TeacherGuestDetail() {
  const { user, loading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [guest, setGuest] = useState(null);
  const [loadingGuest, setLoadingGuest] = useState(true);
  const [resources, setResources] = useState({});

  const load = async () => {
    try {
      const r = await api.get(`/teacher/guests/${id}`);
      setGuest(r.data);
    } catch (err) {
      toast.error(err?.response?.status === 404 ? "This guest isn't assigned to you." : "Couldn't load guest.");
      setGuest(null);
    } finally { setLoadingGuest(false); }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, id]);

  useEffect(() => {
    if (!user) return;
    api.get("/coord/lesson-resources").then((r) => setResources(r.data.resources || {})).catch(() => {});
  }, [user]);

  const toggleSession = async (track, index, completed, date, notes) => {
    const field = TRACK_FIELD[track];
    const updated = { ...guest };
    updated[field] = [...(updated[field] || [])];
    updated[field][index] = { completed, date: completed ? date : null, notes: notes || "" };
    setGuest(updated);
    try {
      await api.put(`/teacher/guests/${id}/progress`, { track, index, completed, date, notes });
    } catch {
      toast.error("Couldn't save. Try again.");
    }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-16" style={{ color: "var(--muted)" }}>Loading…</div>;
  if (!user) return <Navigate to={`/teacher/login?next=/teacher/guests/${id}`} replace />;
  if (!user.is_teacher && !user.is_admin) return <Navigate to="/teacher/login" replace />;

  const overallDone = guest ? TRACKS.reduce((s, t) => {
    const rawDone = (guest[TRACK_FIELD[t.key]] || []).filter((x) => x.completed).length;
    return s + Math.min(rawDone, t.requiredCount ?? t.count);
  }, 0) : 0;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8 lg:py-10" data-testid="teacher-guest-detail">
      <button onClick={() => navigate("/teacher")} className="text-sm mb-6 flex items-center gap-1 hover:underline" style={{ color: "var(--ink-2)" }} data-testid="teacher-back-btn">
        <ArrowLeft size={14} /> Back to my guests
      </button>

      {loadingGuest ? (
        <p style={{ color: "var(--muted)" }}>Loading guest…</p>
      ) : !guest ? (
        <div className="card-surface p-16 text-center">
          <p style={{ color: "var(--muted)" }}>This guest isn't assigned to you.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {!(guest.is_mine || guest.is_mine_next_steps) && (
            <div className="p-4 rounded-md border-l-4" style={{ background: "var(--bg-alt)", borderColor: "var(--gold)", color: "var(--ink-2)" }} data-testid="teacher-readonly-banner">
              <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--gold)" }}>Shared guest</div>
              <p className="text-sm">
                You can update <strong style={{ color: "var(--ink)" }}>Guest Connection</strong> for anyone in your church — the rest of the tracks stay with the assigned teacher.
                {(guest.assigned_teachers?.length || 0) > 0 && <> Phase 1 is with <strong style={{ color: "var(--ink)" }}>{guest.assigned_teachers.map((t) => t.name).join(" & ")}</strong>.</>}
                {(guest.next_steps_teachers?.length || 0) > 0 && <> Next Steps is with <strong style={{ color: "var(--ink)" }}>{guest.next_steps_teachers.map((t) => t.name).join(" & ")}</strong>.</>}
              </p>
            </div>
          )}
          <GuestInfoCard guest={guest} onSaved={load} editable={guest.is_mine || guest.is_mine_next_steps} overallDone={overallDone} />

          <ActivityLogPanel scope="teacher" guestId={guest.id} defaultOpen={false} />

          <div>
            <div className="overline mb-4">Discipleship progress</div>
            {(() => {
              const sessionsFor = (track) => {
                const s = (guest[TRACK_FIELD[track.key]] || []).slice(0, track.count);
                while (s.length < track.count) s.push({ completed: false, date: null, notes: "" });
                return s;
              };
              const guestConnectionTrack = TRACKS.find((t) => t.key === "initial_contact");
              const firstStepsTracks = TRACKS.filter((t) => ["monday", "church", "home"].includes(t.key));
              const nextStepsTrack = TRACKS.find((t) => t.key === "first_steps");
              return (
                <>
                  {guestConnectionTrack && (
                    <section className="mb-8" data-testid="teacher-section-guest-connection">
                      <ConnectionLog scope="teacher" guestId={guest.id} />
                    </section>
                  )}
                  <section className="mb-8" data-testid="teacher-section-first-steps">
                    <div className="mb-4 pb-2 border-b" style={{ borderColor: "var(--border-subtle)" }}>
                      <div className="overline" style={{ color: "var(--terracotta)", fontSize: "0.6rem" }}>Phase 1</div>
                      <h3 className="font-serif-display text-2xl mt-1" style={{ color: "var(--ink)" }}>First Steps</h3>
                    </div>
                    {firstStepsTracks.map((track) => (
                      <ProgressTrack
                        key={track.key}
                        track={track}
                        sessions={sessionsFor(track)}
                        onToggle={toggleSession}
                        resources={resources}
                        editable={!!guest.is_mine}
                        myTeacherId={guest.my_teacher_id || null}
                      />
                    ))}
                  </section>
                  {nextStepsTrack && (
                    <section data-testid="teacher-section-next-steps">
                      <div className="mb-4 pb-2 border-b" style={{ borderColor: "var(--border-subtle)" }}>
                        <div className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Phase 2</div>
                        <h3 className="font-serif-display text-2xl mt-1" style={{ color: "var(--ink)" }}>Next Steps</h3>
                      </div>
                      {guest.phase2_enrolled ? (
                        <ProgressTrack track={nextStepsTrack} sessions={sessionsFor(nextStepsTrack)} onToggle={toggleSession} resources={resources} editable={!!guest.is_mine_next_steps} />
                      ) : (
                        <p className="text-sm italic" style={{ color: "var(--muted)" }}>
                          Not yet enrolled in Next Steps. A coordinator can graduate this guest from the First Steps page.
                        </p>
                      )}
                    </section>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
